/**
 * deform_field — SNOW AND MUD THAT REMEMBER (shader gaps program round 3, weather surfaces, 2026-09-30).
 *
 * The engine had a real deformation heightmap (`src/terraindeform/DeformationHeightmap`: Gaussian sphere / capsule
 * deposits, "the deepest agent wins", a slow refill) that Atlas never wired, while the footprints were timed decals
 * (a snow hollow for 80 s, then gone). After Barré-Brisebois, "Deformable Snow Rendering in Batman: Arkham Origins"
 * (GDC 2014): the field is a camera-following window of depressions fed by every contact, persistent inside the
 * window, replenished while it snows ("subtract a small value … since it's snowing"), read by the ground as a relief
 * (their console path: "virtual displacement via relief mapping … independent from triangle density").
 *
 * THE FIELD IS THE ENGINE'S; ATLAS OWNS THE WINDOW AND THE STAMPS.
 *   • the window: `DEFORM_N`² cells of `DEFORM_CELL_M` around the player, anchored in ABSOLUTE metres (the rebase
 *     moves nothing), slid by WHOLE cells (`DeformationHeightmap.scroll`) once the player is a quarter of it from the
 *     middle — what scrolls out is forgotten (NOTHING PERSISTS PAST THE WINDOW: a chunk eviction, a teleport or a
 *     world change clears it; the far tier and the chunks beyond draw no prints);
 *   • the stamps: a footprint is a CAPSULE heel → toe along the stride's heading (the same stride events the
 *     footprint decals are laid from — the player, remotes, walking versonas); a hovercraft low over snow ploughs a
 *     wide shallow trough; the depth is the SURFACE's (snow by the snow cover, mud by the wetness, dry ground a scuff);
 *   • the clock: fresh snowfall fills the tracks (`snowFillM`), rain settles mud a little (`rainSettleM`).
 *
 * The ground reads the field from the rain-shelter atlas's lower region (`rain_shelter.deformDepthAt`) — the v2
 * ground is at its sampled-texture limit, so the field rides the texture it already binds. How visible a depth is
 * (snow shows all of it, wet soil some, rock and roads none) is the GROUND's decision (`lib/deform_layer.ts`).
 */
import { DeformationHeightmap } from 'enginex/terraindeform/DeformationHeightmap.js';
import { createTerrainDeformConfig } from 'enginex/terraindeform/TerrainDeformConfig.js';
import { WET_TIME_SCALE } from './wet_state.js';
import { activePreset } from './quality_tiers.js';

/** Cells a side and the cell (m): a 38.3 m window at 12 cm — a print is a readable dent, a trail a groove. */
export const DEFORM_N = 320;
export const DEFORM_CELL_M = 0.12;
/** The window's world span between its first and last sample (the engine's grid spans the bounds inclusively). */
export const DEFORM_SPAN_M = (DEFORM_N - 1) * DEFORM_CELL_M;
/** The drawn view: one texel per sample, centred on the samples (half a cell of margin each side). */
export const DEFORM_VIEW_M = DEFORM_N * DEFORM_CELL_M;
/** Slide the window once the player is this far from its middle. */
export const DEFORM_RECENTER_M = DEFORM_SPAN_M * 0.25;
/** The deepest dent any contact makes (m). */
export const DEFORM_MAX_DEPTH_M = 0.12;

/** A boot print: length along the heading, the half-width of the capsule. */
export const PRINT_LEN_M = 0.26;
export const PRINT_RADIUS_M = 0.09;
/** Snow a print presses at full cover, and at the lightest cover that still takes a print. */
export const SNOW_PRINT_M = 0.085;
export const SNOW_PRINT_MIN_M = 0.03;
/** Soil: a dry scuff and a soaked rut. */
export const SOIL_PRINT_DRY_M = 0.004;
export const SOIL_PRINT_WET_M = 0.03;
/** A hovercraft's cushion over snow: a wide shallow trough while it hovers this low. */
export const HOVER_TROUGH_RADIUS_M = 1.1;
export const HOVER_TROUGH_DEPTH_M = 0.035;
export const HOVER_TROUGH_CLEARANCE_M = 1.8;
/** Fresh snow at full intensity fills tracks this fast (m per GAME hour) — a heavy fall is 3–5 cm/h, the storm more. */
export const SNOW_FILL_M_PER_H = 0.1;
/** Rain settles a mud rut this much per mm of rain (m) — a downpour softens a rut over tens of minutes. */
export const RAIN_SETTLE_M_PER_MM = 0.0012;

export type DeformSurface = 'snow' | 'ground' | 'none';

export interface TrackStamp { x0: number; z0: number; x1: number; z1: number; radius: number; depth: number }

/** The depth a print presses on a surface (m): snow by its cover, soil by its wetness; nothing on 'none'. */
export function printDepth(surface: DeformSurface, snowCover01: number, wetness01: number): number {
  if (surface === 'snow') {
    const c = Math.max(0, Math.min(1, snowCover01));
    return SNOW_PRINT_MIN_M + (SNOW_PRINT_M - SNOW_PRINT_MIN_M) * c;
  }
  if (surface === 'ground') {
    const w = Math.max(0, Math.min(1, wetness01));
    return SOIL_PRINT_DRY_M + (SOIL_PRINT_WET_M - SOIL_PRINT_DRY_M) * w * w;
  }
  return 0;
}

/**
 * A print's capsule: centred on the foot, heel → toe along the heading. The heading is three's `rotation.y` yaw the
 * footprint decals use (their length along +z turned by yaw): forward = (sin yaw, 0, cos yaw).
 */
export function printStamp(x: number, z: number, yaw: number, depth: number): TrackStamp {
  const fx = Math.sin(yaw), fz = Math.cos(yaw), h = PRINT_LEN_M / 2 - PRINT_RADIUS_M * 0.5;
  return { x0: x - fx * h, z0: z - fz * h, x1: x + fx * h, z1: z + fz * h, radius: PRINT_RADIUS_M, depth };
}

/** A hovercraft's trough from its last position to its current one, or null when it is too high or not over snow. */
export function hoverStamp(x0: number, z0: number, x1: number, z1: number, clearance: number, snowCover01: number): TrackStamp | null {
  if (!(clearance < HOVER_TROUGH_CLEARANCE_M) || !(snowCover01 > 0.15)) return null;
  const k = (1 - Math.max(0, clearance) / HOVER_TROUGH_CLEARANCE_M) * Math.min(1, snowCover01);
  return { x0, z0, x1, z1, radius: HOVER_TROUGH_RADIUS_M, depth: HOVER_TROUGH_DEPTH_M * k };
}

/** Metres of fresh snow filling the tracks over `dtS` real seconds at a snowfall density 0..1. */
export function snowFillM(dtS: number, snowDensity01: number, timeScale = WET_TIME_SCALE): number {
  const d = Math.max(0, Math.min(1, snowDensity01));
  return (SNOW_FILL_M_PER_H * d * Math.max(0, dtS) * timeScale) / 3600;
}
/** Metres a rut settles over `dtS` real seconds of rain at `rainMmH`. */
export function rainSettleM(dtS: number, rainMmH: number, timeScale = WET_TIME_SCALE): number {
  return (RAIN_SETTLE_M_PER_MM * Math.max(0, rainMmH) * Math.max(0, dtS) * timeScale) / 3600;
}

/** The window's minimum (absolute) for a centre, snapped to the cell lattice so a scroll is always whole cells. */
export function windowMinFor(cx: number, cz: number): [number, number] {
  const snap = (v: number): number => Math.round((v - DEFORM_SPAN_M / 2) / DEFORM_CELL_M) * DEFORM_CELL_M;
  return [snap(cx), snap(cz)];
}
/** Whole cells to slide so the window's middle comes back to the player, or [0, 0] while the player is near it. */
export function scrollCellsFor(minX: number, minZ: number, px: number, pz: number): [number, number] {
  const mx = minX + DEFORM_SPAN_M / 2, mz = minZ + DEFORM_SPAN_M / 2;
  if (Math.abs(px - mx) <= DEFORM_RECENTER_M && Math.abs(pz - mz) <= DEFORM_RECENTER_M) return [0, 0];
  return [Math.round((px - mx) / DEFORM_CELL_M), Math.round((pz - mz) / DEFORM_CELL_M)];
}

/**
 * The live field: the engine heightmap in an ABSOLUTE window. `version` moves on every change (the module re-draws
 * the atlas region only then). Not a singleton — the bed builds its own.
 */
export class DeformField {
  readonly map: DeformationHeightmap;
  stamps = 0;
  scrolls = 0;
  private placed = false;

  constructor() {
    this.map = new DeformationHeightmap(createTerrainDeformConfig({
      quality: { resolution: DEFORM_N, maxDeformers: 1 << 20 },
      bounds: { minX: -DEFORM_SPAN_M / 2, minZ: -DEFORM_SPAN_M / 2, maxX: DEFORM_SPAN_M / 2, maxZ: DEFORM_SPAN_M / 2 },
      maxDepth: DEFORM_MAX_DEPTH_M, refillRate: 0, autoUpload: false,
    }));
  }

  get version(): number { return this.map.version; }
  get bounds(): { minX: number; minZ: number; maxX: number; maxZ: number } { return this.map.config.bounds; }
  /** The drawn view's minimum corner (absolute): the first sample less half a cell. */
  viewMin(): [number, number] { const b = this.bounds; return [b.minX - DEFORM_CELL_M / 2, b.minZ - DEFORM_CELL_M / 2]; }

  /** Follow the player: the first call places the window, later ones slide it by whole cells. Returns whether it moved. */
  follow(px: number, pz: number): boolean {
    const b = this.map.config.bounds;
    if (!this.placed) {
      const [mx, mz] = windowMinFor(px, pz);
      b.minX = mx; b.minZ = mz; b.maxX = mx + DEFORM_SPAN_M; b.maxZ = mz + DEFORM_SPAN_M;
      this.map.clear();
      this.placed = true;
      return true;
    }
    const [di, dj] = scrollCellsFor(b.minX, b.minZ, px, pz);
    if (di === 0 && dj === 0) return false;
    this.map.scroll(di, dj);
    this.scrolls++;
    return true;
  }

  /** Forget everything (a world change, a teleport past the window). The next `follow` places the window afresh. */
  reset(): void { this.map.clear(); this.placed = false; }

  stamp(s: TrackStamp): void {
    if (!(s.depth > 0)) return;
    if (s.x0 === s.x1 && s.z0 === s.z1) this.map.deposit({ kind: 'sphere', x: s.x0, z: s.z0, radius: s.radius, depth: s.depth });
    else this.map.deposit({ kind: 'capsule', x0: s.x0, z0: s.z0, x1: s.x1, z1: s.z1, radius: s.radius, depth: s.depth });
    this.stamps++;
  }

  /** Fill / settle by `amount` metres; returns whether anything changed. */
  relax(amount: number): boolean { return this.map.relax(amount); }

  depthAt(x: number, z: number): number { return this.map.sampleBilinear(x, z); }
}

// ── the runtime (RainShelterModule owns the field; the footprint module feeds it) ──

/** The tier flag (`TierPreset.surfaceDeform`, high+) with the kill switch; it rides the shelter atlas, so it needs the roof map. */
export function deformOn(): boolean {
  try { if (localStorage.getItem('atlas:deform') === 'off') return false; } catch { /* no storage */ }
  const p = activePreset() as { surfaceDeform?: boolean; rainShelter?: boolean };
  return !!p.surfaceDeform && !!p.rainShelter;
}

export const deformRuntime: { field: DeformField | null; on: boolean; strides: number; hover: number } = { field: null, on: false, strides: 0, hover: 0 };

/**
 * A stride from the footprint module: press it into the field. Returns true when the FIELD shows this print (a snow
 * print inside the window) — the snow decal then stands down (the decals are the fallback where the field is off,
 * never a second system on top); a soil print also stamps the field but keeps its scuff decal (dirt, not a dent).
 */
export function deformStride(surface: DeformSurface, x: number, z: number, yaw: number, snowCover01: number, wetness01: number): boolean {
  const f = deformRuntime.field;
  if (!deformRuntime.on || !f || surface === 'none') return false;
  const b = f.bounds, m = PRINT_LEN_M;
  if (x < b.minX + m || x > b.maxX - m || z < b.minZ + m || z > b.maxZ - m) return false;
  const depth = printDepth(surface, snowCover01, wetness01);
  if (!(depth > 0)) return false;
  f.stamp(printStamp(x, z, yaw, depth));
  deformRuntime.strides++;
  return surface === 'snow';
}
